<template>
  <div>
    <q-card flat bordered>
      <q-card-section v-if="showAddSubjectButton">
        <q-btn
          unelevated
          dense
          flat
          no-caps
          color="primary"
          icon="add"
          @click="skippedSubject = false"
        >
          Add Subject
        </q-btn>
      </q-card-section>
      <q-card-section v-else-if="report.subject === null">
        <div class="text-h6 q-mb-md">Enter the Subject Property</div>
        <address-autocomplete
          id="intercom-enter-subject-address"
          placeholder="Enter an address or building name"
          surface-reports
          autofocus
          @new-address="newSubject"
        />
        <q-card-section class="q-px-none row items-center justify-between">
          <q-btn
            active-class="text-primary indented-primary"
            no-caps
            unelevated
            @click="newCustomProperty"
          >
            <div class="flex items-center">
              <q-icon name="add_circle" class="q-mr-sm" />
              New Development
            </div>
          </q-btn>
          <q-btn flat no-caps unelevated color="red" @click="skipSubjectEntry">
            Skip Subject Property
          </q-btn>
        </q-card-section>
      </q-card-section>
      <q-card-section v-if="report.subject || report.comparable_addresses.length">
        <property-compare-google-map
          :subject-detail="subjectDetail"
          :filtered-properties="filteredProperties"
          :loading="loading"
          :similarities="similarities"
          delete-enabled
          @delete-address="deleteAddress"
        />
        <property-compare-building-info
          :subject-detail="subjectDetail"
          :filtered-properties="filteredProperties"
          :loading="loading"
          :similarities="similarities"
          :date-range="dateRange"
          :skeleton-rows="report.comparable_addresses.length + (report.subject !== null ? 1 : 0)"
          :report-id="report.id"
          :visible-columns="
            getVisibleColumnsForSection(visibleColumns, Section.BUILDING_INFORMATION)
          "
          :toggleable-columns="getToggleableColumnsForSection(Section.BUILDING_INFORMATION)"
          delete-enabled
          class="q-mt-md"
          @delete-address="deleteAddress"
          @move-comp="moveComp"
          @update-property-details="updatePropertyDetails"
        />
      </q-card-section>
      <q-card-section v-if="preventAddingSingleComp">
        <warning-banner class="q-mb-md">
          For faster loading speeds, we do not allow reports with more than
          {{ maxCompLimit }} comps.
        </warning-banner>
      </q-card-section>
      <q-card-section v-else-if="report.subject || skippedSubject" id="add-rent-comps">
        <div class="flex justify-between q-mb-md">
          <div class="text-h6">Rent Comps</div>
          <div class="q-gutter-x-sm">
            <q-btn no-caps unelevated @click="advancedMode = !advancedMode">
              Settings ({{ countFilters }})
              <slot name="after">
                <q-icon v-if="!advancedMode" name="arrow_drop_down" />
                <q-icon v-else name="arrow_drop_up" />
              </slot>
            </q-btn>
            <q-btn
              no-caps
              unelevated
              :disable="
                report.excluded_property_ids.length + report.comparable_addresses.length === 0
              "
              @click="resetSelections()"
            >
              Reset Selections
              <q-tooltip
                v-if="
                  report.excluded_property_ids.length + report.comparable_addresses.length === 0
                "
                >No properties to reset.</q-tooltip
              >
              <q-tooltip v-else>
                Reset the selections for the subject and comps. This will remove all properties from
                the report.
              </q-tooltip>
            </q-btn>
            <q-btn
              id="intercom-suggest-comps"
              :disable="!report.subject"
              :loading="compsLoading"
              :percentage="percentageLoaded"
              :label="`Suggest${report.comparable_addresses.length > 0 && report.subject ? ' More' : ''} Comps`"
              no-caps
              unelevated
              split
              color="primary"
              class="relative-position"
              @click="suggestComps()"
            >
              <ping-dot v-if="$route.hash === '#add-rent-comps' && showButtonPingDot" />
            </q-btn>
            <q-tooltip v-if="!report.subject">Enter the subject to enable this feature.</q-tooltip>
          </div>
        </div>

        <q-card
          v-if="advancedMode"
          flat
          class="q-mb-md"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <!-- Allow users to customize comps settings-->
          <q-card-section>
            <div>Customize Settings</div>
            <div>
              Overwrite the recommended settings for comp selection. Click "Suggest Comps" to get
              new comps based on these settings.
            </div>
          </q-card-section>

          <div class="flex q-gutter-x-md q-px-md items-center">
            <q-input
              v-model.number="customMaxRadius"
              no-error-icon
              label="Max Search Radius (miles)"
              dense
              outlined
              clearable
              type="number"
              :rules="[
                (val) => val === null || val >= 0 || 'Must be a positive number',
                (val) => val === null || val <= 20 || 'Must be less than 20 miles'
              ]"
            />
            <q-input
              v-model.number="customMinUnits"
              no-error-icon
              label="Min Units"
              dense
              outlined
              clearable
              type="number"
              :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
            />

            <q-input
              v-model.number="customMaxUnits"
              no-error-icon
              label="Max Units"
              dense
              outlined
              clearable
              type="number"
              :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
            />

            <q-input
              v-model.number="customMinYearBuilt"
              no-error-icon
              label="Min Year Built"
              dense
              outlined
              clearable
              type="number"
              :rules="[
                (val) => val === null || val >= 0 || 'Must be a positive number',
                (val) => val === null || val >= 1800 || 'Must be more than 1800'
              ]"
            />

            <q-input
              v-model.number="customMaxYearBuilt"
              no-error-icon
              label="Max Year Built"
              dense
              outlined
              clearable
              type="number"
              :rules="[
                (val) => val === null || val >= 0 || 'Must be a positive number',
                (val) => val === null || val <= 2030 || 'Must be less than 2030'
              ]"
            />

            <q-input
              v-model.number="customMinNumberStories"
              no-error-icon
              label="Min Number of Stories"
              dense
              outlined
              clearable
              type="number"
              :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
            />

            <q-input
              v-model.number="customMaxNumberStories"
              no-error-icon
              label="Max Number of Stories"
              dense
              outlined
              clearable
              type="number"
              :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
            />
            <span>
              <q-toggle
                v-model="customValueAdd"
                label="Value Add"
                color="primary"
                dense
                class="q-mb-lg"
              />
              <q-tooltip>
                When enabled, the comp results will assume improved conditions for the properties,
                generating the next level of quality and condition.
              </q-tooltip>
            </span>
            <span>
              <q-toggle
                v-model="customIsAffordable"
                toggle-indeterminate
                label="Affordable"
                color="primary"
                checked-icon="check"
                unchecked-icon="close"
                dense
                class="q-mb-lg"
              />
              <q-tooltip>
                Whether to exclude, include or be indifferent to affordable properties.
              </q-tooltip>
            </span>
            <span>
              <q-toggle
                v-model="customIsStudent"
                toggle-indeterminate
                label="Student"
                color="primary"
                checked-icon="check"
                unchecked-icon="close"
                dense
                class="q-mb-lg"
              />
              <q-tooltip>
                Whether to include only, exclude or be indifferent to student properties.
              </q-tooltip>
            </span>
            <span>
              <q-toggle
                v-model="customIsSenior"
                toggle-indeterminate
                label="Senior"
                color="primary"
                checked-icon="check"
                unchecked-icon="close"
                dense
                class="q-mb-lg"
              />
              <q-tooltip>
                Whether to include only, exclude or be indifferent to senior properties.
              </q-tooltip>
            </span>
            <q-toggle
              v-if="hasHelloDataFlag"
              v-model="customUseEmbedding"
              label="Use Embedding"
              color="primary"
              dense
              class="q-mb-lg"
            >
              <q-tooltip> Use embedding for similarity scoring. </q-tooltip>
              <q-badge color="primary" class="q-ml-sm">Internal</q-badge>
            </q-toggle>
          </div>
        </q-card>

        <address-autocomplete
          id="intercom-enter-comp-address"
          :placeholder="`Enter an${
            report.comparable_addresses.length > 0 ? 'other' : ''
          } address or building name`"
          :lat="subjectDetail?.lat"
          :lon="subjectDetail?.lon"
          @new-address="newAddress"
        />
      </q-card-section>
    </q-card>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { addDays } from 'date-fns'

import {
  type OrganizationAttributes,
  type PropertyDetailsResponse,
  type PropertySearchResponse,
  type RentSourceSubscriptionAttributes,
  Section,
  SectionColumn,
  FeatureFlag,
  getLatestPricesByFilter,
  overwritePropertyData,
  getUnitsByFilter,
  type Similarities,
  maxCompLimit
} from 'common'

import AddressAutocomplete from './AddressAutocomplete.vue'
import { useRentsourceStore } from '@/stores/rentsource'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { formatPercent, getToggleableColumnsForSection, getVisibleColumnsForSection } from '@/utils'
import PropertyCompareGoogleMap from './PropertyCompareGoogleMap.vue'
import PropertyCompareBuildingInfo from './PropertyCompareBuildingInfo.vue'
import { useUserStore } from '@/stores/user'
import { scroll } from 'quasar'
import PingDot from '@/components/PingDot.vue'
import { isLoaded } from '@/utils/loading'
import WarningBanner from '@/components/WarningBanner.vue'
const { getScrollTarget, setVerticalScrollPosition } = scroll

const moveArrayElement = <T,>(arr: T[], from: number, to: number): T[] => {
  const element = arr[from]
  const withoutElement = [...arr.slice(0, from), ...arr.slice(from + 1)]
  return [...withoutElement.slice(0, to), element, ...withoutElement.slice(to)]
}

export default {
  name: 'EnterAddresses',
  components: {
    WarningBanner,
    PingDot,
    AddressAutocomplete,
    PropertyCompareGoogleMap,
    PropertyCompareBuildingInfo
  },
  props: {
    report: {
      type: Object as PropType<RentSourceSubscriptionAttributes>,
      required: true
    }
  },
  emits: ['update-property-details', 'update-report', 'new-custom-property'],
  data() {
    return {
      Section,
      formatPercent,
      maxCompLimit,
      compsLoading: false,
      compsLoadingState: null as { current: number; total: number } | null,
      loading: true,
      showButtonPingDot: true,
      subjectDetail: null as PropertyDetailsResponse | null,
      comparableDetails: [] as PropertyDetailsResponse[],
      similarities: {} as { [key: string]: Similarities },
      advancedMode: false,
      customMaxRadius: null as number | null,
      customMinUnits: null as number | null,
      customMaxUnits: null as number | null,
      customMinYearBuilt: null as number | null,
      customMaxYearBuilt: null as number | null,
      customMinNumberStories: null as number | null,
      customMaxNumberStories: null as number | null,
      customValueAdd: false,
      customUseEmbedding: false,
      customIsAffordable: null as boolean | null,
      customIsSenior: null as boolean | null,
      customIsStudent: null as boolean | null,
      dateRange: {
        from: addDays(new Date(), -30),
        to: new Date()
      },
      reportCopy: this.report,
      skippedSubject: false
    }
  },
  computed: {
    percentageLoaded() {
      if (this.compsLoadingState) {
        return (this.compsLoadingState.current / this.compsLoadingState.total) * 100
      }
      return undefined
    },
    hasHelloDataFlag() {
      const report = usePropertyReportStore().activeReport
      if (isLoaded(report)) {
        return useUserStore().hasFeatureFlag(report.data, FeatureFlag.HELLODATA)
      }
      return false
    },
    allProperties() {
      if (this.subjectDetail) {
        return [this.subjectDetail, ...this.comparableDetails]
      }
      return this.comparableDetails
    },
    filteredProperties() {
      return this.allProperties.flatMap((prop) => {
        return {
          prop,
          units: getUnitsByFilter(
            prop,
            getLatestPricesByFilter(prop, this.dateRange.from, this.dateRange.to),
            this.dateRange.to,
            'all'
          )
        }
      })
    },
    countFilters() {
      return [
        this.customMaxRadius,
        this.customMinUnits,
        this.customMaxUnits,
        this.customMinYearBuilt,
        this.customMaxYearBuilt,
        this.customMinNumberStories,
        this.customMaxNumberStories
      ].filter((_) => _ !== null).length
    },
    user() {
      return this.$auth0.user?.value
    },
    visibleColumns(): SectionColumn[] {
      const org = useUserStore().organization as OrganizationAttributes | undefined
      if (org?.columns) {
        return org.columns
      }
      return []
    },
    showAddSubjectButton() {
      return this.skippedSubject && this.report.subject === null
    },
    preventAddingSingleComp() {
      return this.comparableDetails.length > this.maxCompLimit - 1
    }
  },
  created() {
    if (this.report.subject) {
      this.updateSubject().then(() => {
        if (this.report.id === -1) {
          // auto-load comps if we are creating a new report
          // with a seeded subject
          this.suggestComps()
        }
      })
    }
    // If a report doesn't have a subject but has at least one comp, it means they skipped the subject previously
    if (!this.report.subject && this.report.comparable_addresses.length > 0) {
      this.skippedSubject = true
    }
    if (this.comparableDetails.length !== this.report.comparable_addresses.length) {
      this.updateComparables()
    }
  },
  mounted() {
    if (this.$route.hash) {
      const el = document.querySelector(this.$route.hash)
      if (el) {
        const target = getScrollTarget(el)
        const offset = el.offsetTop
        const duration = 1000
        setVerticalScrollPosition(target, offset, duration)
      }
    }
  },
  methods: {
    getToggleableColumnsForSection,
    getVisibleColumnsForSection,
    moveComp(fromIndex: number, toIndex: number) {
      if (fromIndex === toIndex) return

      fromIndex = fromIndex - 1
      toIndex = toIndex - 1

      this.reportCopy.comparable_addresses = moveArrayElement(
        this.reportCopy.comparable_addresses,
        fromIndex,
        toIndex
      )

      this.comparableDetails = moveArrayElement(this.comparableDetails, fromIndex, toIndex)

      this.$emit('update-report', this.reportCopy)
      this.$tracker.movedComp(fromIndex, toIndex)
    },
    refreshSimilarities() {
      if (this.subjectDetail && this.comparableDetails.length) {
        this.similarities = {}
        useRentsourceStore()
          .comparables(this.subjectDetail, {
            excludedIds: this.report.excluded_property_ids,
            selectedIds: this.report.comparable_addresses.map((_) => _.id),
            topN:
              this.report.comparable_addresses.length > maxCompLimit
                ? maxCompLimit
                : this.report.comparable_addresses.length,
            renovations: this.customValueAdd ? 0.2 : undefined
          })
          .then((comps) => {
            comps.comparables.forEach((comp) => {
              this.similarities[comp.id] = comp.similarity_score
            })
          })
      }
    },
    resetSelections() {
      this.reportCopy.excluded_property_ids = []
      this.reportCopy.comparable_addresses = []
      this.comparableDetails = []
      this.$emit('update-report', this.reportCopy)
    },
    updatePropertyDetails(propertyId: string, key: string, value: string | number) {
      // change subjectDetail or comparableDetails property's key to value
      if (this.subjectDetail && this.subjectDetail.id === propertyId) {
        this.subjectDetail[key] = value
      } else {
        const detail = this.comparableDetails.find((_) => _.id === propertyId)
        if (detail) {
          detail[key] = value
        }
      }
      this.$emit('update-property-details', propertyId, key, value)
    },
    updateComparables() {
      const existingDetailIds = new Set(this.comparableDetails.map((_) => _.id))
      const todo = this.report.comparable_addresses.filter(
        (address) => !existingDetailIds.has(address.id)
      )

      this.compsLoadingState = {
        current: 0,
        total: todo.length
      }

      if (todo.length) {
        Promise.all(
          todo.map((_) =>
            useRentsourceStore()
              .details(_.id)
              .finally(() => {
                if (this.compsLoadingState) {
                  this.compsLoadingState.current += 1
                }
              })
          )
        )
          .then((details) => {
            const combined = this.comparableDetails.concat(
              details.filter((_) => _) as PropertyDetailsResponse[]
            )
            const compsDetails = this.report.comparable_addresses
              .map((address) => combined.find((_) => _.id === address.id))
              .filter((_) => _) as PropertyDetailsResponse[]

            compsDetails.forEach((comp) => {
              overwritePropertyData(comp, this.report?.property_edits ?? [])
            })
            this.comparableDetails = compsDetails
            this.checkLoading()
            this.refreshSimilarities()
          })
          .then(() => {
            this.compsLoading = false
            this.compsLoadingState = null
          })
      } else {
        this.comparableDetails = this.report.comparable_addresses
          .map((address) => {
            return this.comparableDetails.find((_) => _.id === address.id)
          })
          .filter((_) => _) as PropertyDetailsResponse[]
        this.refreshSimilarities()
        this.compsLoading = false
        this.compsLoadingState = null
      }
    },
    async updateSubject() {
      if (this.report.subject && this.subjectDetail?.id !== this.report.subject.id) {
        return useRentsourceStore()
          .details(this.report.subject.id)
          .then((prop) => {
            prop = overwritePropertyData(prop, this.report?.property_edits ?? [])

            this.subjectDetail = prop
            this.checkLoading()
            this.refreshSimilarities()
          })
          .catch((error) => {
            console.error(error)
          })
      }
    },
    newCustomProperty() {
      this.$emit('new-custom-property', {
        name: 'New Development',
        query: { gotoEditNext: true }
      })
    },
    suggestComps() {
      if (!this.subjectDetail) {
        return
      }
      this.showButtonPingDot = false
      this.compsLoading = true

      this.$tracker.requestedComps({
        maxRadius: this.customMaxRadius,
        minUnits: this.customMinUnits,
        maxUnits: this.customMaxUnits,
        minYearBuilt: this.customMinYearBuilt,
        maxYearBuilt: this.customMaxYearBuilt,
        valueAdd: this.customValueAdd,
        isAffordable: this.customIsAffordable,
        isSenior: this.customIsSenior,
        isStudent: this.customIsStudent,
        minNumberStories: this.customMinNumberStories,
        maxNumberStories: this.customMaxNumberStories
      })

      const amountFromLimit = this.maxCompLimit - this.report.comparable_addresses.length
      const topN =
        amountFromLimit >= 10 ? this.report.comparable_addresses.length + 10 : this.maxCompLimit
      useRentsourceStore()
        .comparables(this.subjectDetail, {
          excludedIds: this.report.excluded_property_ids,
          selectedIds: this.report.comparable_addresses.map((_) => _.id),
          topN: topN,
          renovations: this.customValueAdd ? 0.2 : undefined,
          maxDistance: this.customMaxRadius || undefined,
          minNumberUnits: this.customMinUnits || undefined,
          maxNumberUnits: this.customMaxUnits || undefined,
          minYearBuilt: this.customMinYearBuilt || undefined,
          maxYearBuilt: this.customMaxYearBuilt || undefined,
          minNumberStories: this.customMinNumberStories || undefined,
          maxNumberStories: this.customMaxNumberStories || undefined,
          useEmbedding: this.customUseEmbedding,
          isAffordable: this.customIsAffordable ?? undefined,
          isSenior: this.customIsSenior ?? undefined,
          isStudent: this.customIsStudent ?? undefined
        })
        .then((resp) => {
          if (resp.comparables.length === 0) {
            this.$q.notify({
              message: 'No comps found. Try adjusting your settings.',
              type: 'positive',
              position: 'top'
            })
          }
          const newComps = resp.comparables.map((comp) => {
            const payload = {
              building_name: comp.building_name,
              building_name_alias: comp.building_name_alias,
              city: comp.city,
              id: comp.id,
              lat: comp.lat,
              lon: comp.lon,
              state: comp.state,
              street_address: comp.street_address,
              street_address_alias: comp.street_address_alias,
              year_built: comp.year_built,
              number_units: comp.number_units,
              zip_code: comp.zip_code
            } as PropertySearchResponse
            this.similarities[payload.id] = comp.similarity_score
            return payload
          })
          this.newAddresses(newComps)
        })
        .catch((e) => {
          this.compsLoading = false
        })
    },
    newAddresses(payload: PropertySearchResponse[]) {
      const filteredNewAddresses = payload.filter(
        (addr) => this.report.comparable_addresses.findIndex((_) => _.id === addr.id) < 0
      )

      this.reportCopy.comparable_addresses =
        this.reportCopy.comparable_addresses.concat(filteredNewAddresses)

      this.reportCopy.excluded_property_ids = this.reportCopy.excluded_property_ids.filter(
        (_) => !filteredNewAddresses.find((addr) => addr.id === _)
      )
      this.updateComparables()
      this.$emit('update-report', this.reportCopy)
    },
    newAddress(payload: PropertySearchResponse) {
      if (!this.preventAddingSingleComp) {
        if (this.subjectDetail?.id === payload.id) {
          this.$q.notify({
            message: 'Cannot add comp that is the same property as the subject.',
            color: 'negative',
            position: 'bottom'
          })
        } else {
          this.newAddresses([payload])
        }
      }
    },
    newSubject(payload: PropertySearchResponse) {
      const comp = this.report.comparable_addresses.find((addr) => addr.id === payload.id)
      if (comp) {
        this.deleteAddress(comp.id)
      }
      this.reportCopy.subject = payload
      this.updateSubject()
      this.$emit('update-report', this.reportCopy)
    },
    deleteAddress(rowId: string) {
      if (this.subjectDetail && rowId === this.subjectDetail.id) {
        this.reportCopy.subject = null
        this.subjectDetail = null
        this.similarities = {}
        if (this.report.comparable_addresses.length > 0) {
          this.skippedSubject = true
        }
      } else {
        this.reportCopy.comparable_addresses = this.reportCopy.comparable_addresses.filter(
          (_) => _.id !== rowId
        )
        this.reportCopy.excluded_property_ids = this.reportCopy.excluded_property_ids.concat([
          rowId
        ])
        this.comparableDetails = this.comparableDetails.filter((_) => _.id !== rowId)
        this.$tracker.excludedComp(this.similarities[rowId])
      }
      this.$emit('update-report', this.reportCopy)
    },
    checkLoading() {
      if (this.comparableDetails.length === this.report.comparable_addresses.length) {
        if (!this.report.subject || this.subjectDetail) {
          this.loading = false
        }
      }
    },
    skipSubjectEntry() {
      this.$tracker.skippedSubjectEntry()
      this.skippedSubject = true
    }
  }
}
</script>
<style scoped>
.property-compare {
  max-width: 1600px;
}
</style>
